import { type SWRInfiniteConfiguration } from 'swr/infinite';
import { useMemo } from 'react';
import {
  type PaginationQuery,
  type TransactionHistoryItem,
} from '@mls-wallet/shared-types';
import { type PaginatedResponse, usePagination } from '@/hooks/use-pagination';

type HistoricTransactionsResponse = PaginatedResponse<TransactionHistoryItem>;

/**
 * Historic-specific wrapper around the generic pagination hook.
 *
 * Notes:
 * - `refreshInterval` and the rest of the SWR options are passed through for
 *   hook compatibility with polling-based consumers.
 * - `enabled` lets tabbed UIs defer fetching until the historic tab is active.
 */
type UseHistoricTransactionsOptions = {
  refreshInterval?: SWRInfiniteConfiguration<HistoricTransactionsResponse>['refreshInterval'];
  pagination?: PaginationQuery;
  enabled?: boolean;
} & SWRInfiniteConfiguration<HistoricTransactionsResponse>;

/**
 * Fetch proposals and confirmed indexed transfers from the session-auth endpoint.
 *
 * Historic remains on its own route so the backend router can inject the
 * fixed historic listing parameters without leaking them into ui-web.
 */
export function useHistoricTransactions(
  options?: UseHistoricTransactionsOptions
) {
  const { pagination, enabled, ...swr } = options ?? {};

  const result = usePagination<TransactionHistoryItem>({
    // One cursor pages the mixed feed; proposal selection/signing still uses
    // only the proposal subset, never an indexed transfer's chain identity.
    path: 'transactions/historic/activity',
    ...(pagination ? { pagination } : {}),
    ...(enabled !== undefined ? { enabled } : {}),
    ...(Object.keys(swr).length > 0
      ? {
          swr,
        }
      : {}),
  });

  const transactions = useMemo(
    () =>
      result.data.flatMap((item) =>
        item.kind === 'proposal' ? [item.transaction] : []
      ),
    [result.data]
  );
  const indexedMovements = useMemo(
    () =>
      result.data.flatMap((item) =>
        item.kind === 'movement' ? [item.movement] : []
      ),
    [result.data]
  );
  return {
    transactions,
    indexedMovements,
    pagination: result.pagination,
    hasMore: result.hasMore,
    isLoading: result.isLoading,
    isLoadingMore: result.isLoadingMore,
    error: result.error,
    loadMore: result.loadMore,
    refetch: result.refetch,
    reset: result.reset,
  };
}
